import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { toArray } from 'lodash-es';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DeviceAppVersionCard } from '../device-app-version-card/DeviceAppVersionCard.ctrl';
import { useSystemPackageVersionDefinitionSection } from '../system-package-version-definition-section/useSystemPackageVersionDefinitionSection.vm';
import {
  SystemPackageVersionDropdownField,
  SystemPackageVersionSelectField,
} from '../system-package-version-fields/SystemPackageVersionFields.ctrl';
import type { SystemPackageVersionAboutSectionProps } from './systemPackageVersionAboutSection.model';
import styles from './systemPackageVersionAboutSection.module.scss';
import { checkMinimumDependencyFieldIsDisabled } from './systemPackageVersionAboutSection.util';

export function SystemPackageVersionAboutSection({
  control,
  hiddenFields,
  operationalSystemDependencyOptions,
  minimumOSVersionOptions,
  systemPackageDependencyOptions,
  minimumSystemPackageVersionOptions,
  appDependencyOptions,
  minimumAppVersionOptions,
  fieldsLoading,
}: SystemPackageVersionAboutSectionProps): ReactNode {
  const { t } = useTranslation(ALL_LNGS_NAMESPACES.APPLICATION);
  const { disabledFields, hiddenFieldsMap } = useSystemPackageVersionDefinitionSection({ control, hiddenFields });

  const shouldNotHideOperationSystemOptions = useMemo(
    () =>
      !(hiddenFieldsMap['definition.operationalSystemDependency'] && hiddenFieldsMap['definition.minimumOSVersion']),
    [hiddenFieldsMap],
  );
  const shouldNotHideSystemPackageVersionOptions = useMemo(
    () =>
      !(
        hiddenFieldsMap['definition.systemPackageDependency'] &&
        hiddenFieldsMap['definition.minimumSystemPackageVersion']
      ),
    [hiddenFieldsMap],
  );

  const shouldNotHideAppDependency = useMemo(
    () => !(hiddenFieldsMap['definition.appDependency'] && hiddenFieldsMap['definition.minimumAppVersion']),
    [hiddenFieldsMap],
  );

  return (
    <DeviceAppVersionCard title={t('title.deploymentInfoSection')}>
      {shouldNotHideOperationSystemOptions && (
        <div className={styles.inputSection}>
          <CoreTypography customClassName={styles.sectionTitle} variant={CoreTypographyVariant.Title03Medium}>
            {t('subtitle.operatingSystem')}
          </CoreTypography>
          {hiddenFieldsMap['definition.operationalSystemDependency'] ? undefined : (
            <SystemPackageVersionDropdownField
              control={control}
              isLoading={fieldsLoading?.['definition.operationalSystemDependency']}
              label={t('input.operationalSystemDependency.label')}
              name="definition.operationalSystemDependency"
              options={toArray(operationalSystemDependencyOptions)}
              required
            />
          )}
          {hiddenFieldsMap['definition.minimumOSVersion'] ? undefined : (
            <SystemPackageVersionSelectField
              control={control}
              disabled={checkMinimumDependencyFieldIsDisabled(disabledFields, 'definition.minimumOSVersion')}
              isLoading={fieldsLoading?.['definition.minimumOSVersion']}
              label={t('input.minimumOSVersion.label')}
              name="definition.minimumOSVersion"
              options={toArray(minimumOSVersionOptions)}
              required
            />
          )}
        </div>
      )}

      {shouldNotHideSystemPackageVersionOptions && (
        <div className={styles.inputSection}>
          <CoreTypography customClassName={styles.sectionTitle} variant={CoreTypographyVariant.Title03Medium}>
            {t('subtitle.systemPackage')}
          </CoreTypography>
          {hiddenFieldsMap['definition.systemPackageDependency'] ? undefined : (
            <SystemPackageVersionDropdownField
              control={control}
              isLoading={fieldsLoading?.['definition.systemPackageDependency']}
              label={t('input.systemPackageDependency.label')}
              name="definition.systemPackageDependency"
              options={toArray(systemPackageDependencyOptions)}
            />
          )}
          {hiddenFieldsMap['definition.minimumSystemPackageVersion'] ? undefined : (
            <SystemPackageVersionSelectField
              control={control}
              disabled={checkMinimumDependencyFieldIsDisabled(disabledFields, 'definition.minimumSystemPackageVersion')}
              isLoading={fieldsLoading?.['definition.minimumSystemPackageVersion']}
              label={t('input.minimumSystemPackageVersion.label')}
              name="definition.minimumSystemPackageVersion"
              options={toArray(minimumSystemPackageVersionOptions)}
              required={
                !checkMinimumDependencyFieldIsDisabled(disabledFields, 'definition.minimumSystemPackageVersion')
              }
            />
          )}
        </div>
      )}

      {shouldNotHideAppDependency && (
        <div className={styles.inputSection}>
          <CoreTypography customClassName={styles.sectionTitle} variant={CoreTypographyVariant.Title03Medium}>
            {t('subtitle.application')}
          </CoreTypography>
          {hiddenFieldsMap['definition.appDependency'] ? undefined : (
            <SystemPackageVersionDropdownField
              control={control}
              isLoading={fieldsLoading?.['definition.appDependency']}
              label={t('input.appDependency.label')}
              name="definition.appDependency"
              options={toArray(appDependencyOptions)}
            />
          )}
          {hiddenFieldsMap['definition.minimumAppVersion'] ? undefined : (
            <SystemPackageVersionSelectField
              control={control}
              disabled={checkMinimumDependencyFieldIsDisabled(disabledFields, 'definition.minimumAppVersion')}
              isLoading={fieldsLoading?.['definition.minimumAppVersion']}
              label={t('input.minimumAppVersion.label')}
              name="definition.minimumAppVersion"
              options={toArray(minimumAppVersionOptions)}
              required={!checkMinimumDependencyFieldIsDisabled(disabledFields, 'definition.minimumAppVersion')}
            />
          )}
        </div>
      )}
    </DeviceAppVersionCard>
  );
}
